import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Document } from "components/icons/v2/Document";
import { RightArrow } from "components/icons/RightArrow";
import { ChangeEvent, ReactNode, useState } from "react";
import { useOnboardingData } from "../../OnboardingDataContext";
import {
  LLC_TAX_OPTIONS,
  MAIN_ENTITY_OPTIONS,
  MainEntity,
  resolveEntity,
} from "./entityOptions";
import { TAXED_AS, TaxedAs } from "v2/constants/entityTypes";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";

type EntityTypeStepProps = {
  onComplete: (summary: ReactNode) => void;
};

export const buildEntityTypeSummary = (label: string): ReactNode => (
  <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60">
    <span className="t-flex t-shrink-0 t-text-purple-70 t-bg-surface-purple t-p-1 t-rounded">
      <Document />
    </span>
    <span>
      <span className="t-text-text-100">Entity type: </span>
      {label}
    </span>
  </div>
);

export const EntityTypeStep = ({ onComplete }: EntityTypeStepProps) => {
  const { data, patchData } = useOnboardingData();

  const [main, setMain] = useState<MainEntity | null>(
    (data.entityTypeMain as MainEntity) || null
  );
  const [taxedAs, setTaxedAs] = useState<TaxedAs | null>(
    (data.entityTypeTaxedAs as TaxedAs) || null
  );

  const handleMainChange = (value: string) => {
    setMain(value as MainEntity);
    setTaxedAs(null);
    patchData({ entityTypeMain: value, entityTypeTaxedAs: "" });
  };

  const handleScorpChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextTaxedAs = event.target.checked ? TAXED_AS.S_CORP : null;
    setTaxedAs(nextTaxedAs);
    patchData({ entityTypeTaxedAs: nextTaxedAs ?? "" });
  };

  const handleLlcTaxChange = (value: string) => {
    setTaxedAs(value as TaxedAs);
    patchData({ entityTypeTaxedAs: value });
  };

  const resolution = resolveEntity(main, taxedAs);

  const handleContinue = () => {
    if (!resolution) return;

    patchData({
      entityTypeId: resolution.entityTypeId,
      entityTypeLabel: resolution.label,
      entityTypeTaxedAs: resolution.taxedAs ?? "",
    });
    onComplete(buildEntityTypeSummary(resolution.label));
  };

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <RadioGroup.Root
        value={main ?? ""}
        onValueChange={handleMainChange}
        className="t-flex t-gap-3 sm:t-flex-col"
      >
        {MAIN_ENTITY_OPTIONS.map((option) => (
          <label
            // @ts-expect-error forHtml is a non-standard prop carried over from
            // the original markup; htmlFor would be the correct attribute.
            forHtml={option.key}
            className="t-group all:unset t-w-full t-box-border t-cursor-pointer t-rounded-lg t-border t-border-solid t-border-neutral-10 t-text-left t-transition-colors t-p-3 t-flex t-flex-col t-gap-1"
            key={option.key}
          >
            <RadioGroup.Item value={option.key} />
            <div className="t-text-body t-text-text-60">{option.name}</div>
            <div className="t-text-body-sm t-text-text-30 t-font-light">
              {option.desc}
            </div>
          </label>
        ))}
      </RadioGroup.Root>

      {main === "corp" && (
        <div className="t-pt-4">
          <Checkbox
            name="corp-scorp"
            label={
              <span className="t-text-text-60 t-flex t-gap-1 t-items-center">
                <span className="">My corporation is taxed as an S-Corp</span>
                <ToolTip text="A corporation that has elected pass-through taxation, where profits and losses flow to shareholders' personal tax returns rather than at the corporate level.">
                  <span className="t-text-text-30 t-flex">
                    <Info size="14" />
                  </span>
                </ToolTip>
              </span>
            }
            checked={taxedAs === TAXED_AS.S_CORP}
            onChange={handleScorpChange}
          />
        </div>
      )}

      {main === "llc" && (
        <div className="t-pt-4">
          <div className="t-mb-2.5 t-text-subtext-sm t-text-text-60">
            How is your LLC taxed?
          </div>
          <RadioGroup.Root
            value={taxedAs ?? ""}
            onValueChange={handleLlcTaxChange}
            className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-2"
          >
            {LLC_TAX_OPTIONS.map((option) => (
              // <RadioGroup.TileItem
              //   key={option.key}
              //   value={option.key}
              //   title={option.name}
              //   subtext={option.desc}
              // />
              <label
                // @ts-expect-error forHtml is a non-standard prop carried over
                // from the original markup; htmlFor would be the correct attribute.
                forHtml={option.key}
                className="t-group all:unset t-w-full t-box-border t-cursor-pointer t-rounded-lg t-border t-border-solid t-border-neutral-10 t-text-left t-transition-colors t-p-3 t-flex t-flex-col t-gap-1"
                key={option.key}
              >
                <RadioGroup.Item value={option.key} />
                <div className="t-text-body t-text-text-60">{option.name}</div>
                <div className="t-text-body-sm t-text-text-30 t-font-light">
                  {option.desc}
                </div>
              </label>
            ))}
          </RadioGroup.Root>
        </div>
      )}

      <div>
        <Button
          customType="primary"
          disabled={!resolution}
          onClick={handleContinue}
          rightIcon={<RightArrow />}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
